'use client';

import { Loader2 } from 'lucide-react';
import { useGetReplies } from '../../hooks/useComments';
import type { Comment, User } from '../../types/comments';
import { CommentCard } from './comment-card';

interface CommentRepliesProps {
  parentId: number;
  threadId: number;
  currentUser: User;
  users: User[];
  variant?: 'sidebar' | 'overlay';
  onReaction: (commentId: number, emoji: string) => void;
  /** Called with (commentId, parentId) so the caller can invalidate the right cache */
  onDelete: (commentId: number, parentId: number) => void;
  onEdit: (comment: Comment) => void;
  isReadOnly?: boolean;
}

export function CommentReplies({
  parentId,
  threadId: _threadId,
  currentUser,
  users,
  variant = 'sidebar',
  onReaction,
  onDelete,
  onEdit,
  isReadOnly,
}: CommentRepliesProps) {
  const { data: rawReplies = [], isLoading } = useGetReplies(parentId);

  const replies = rawReplies
    .filter((r): r is NonNullable<typeof r> => r !== null)
    .map((r) => {
      const author = users.find((u) => u.id === r.createdBy) || {
        id: r.createdBy,
        fullName: 'Unknown User',
        avatarUrl: null,
        email: '',
      };
      return {
        id: r.id,
        threadId: r.threadId,
        parentId: r.parentId ?? null,
        content: r.content,
        reactions: (r.reactions as { emoji: string; userId: number }[]) || [],
        isPinned: !!r.isPinned,
        hasAttachments: !!r.hasAttachments,
        createdBy: r.createdBy,
        createdAt: r.createdAt,
        author,
      } as Comment;
    });

  if (isLoading) {
    return (
      <div className="ml-12 flex items-center gap-2 py-2 text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
        <span className="text-[11px]">Loading replies...</span>
      </div>
    );
  }

  if (replies.length === 0) return null;

  return (
    <div className="ml-9 border-l-2 border-border/60 pl-3 flex flex-col gap-0.5">
      {replies.map((reply) => (
        <CommentCard
          key={reply.id}
          comment={reply}
          currentUser={currentUser}
          users={users}
          variant={variant}
          isReply
          isReadOnly={isReadOnly}
          onReaction={onReaction}
          onDelete={(id) => onDelete(id, parentId)}
          onEdit={onEdit}
        />
      ))}
    </div>
  );
}
